"use client";

import { useEffect, useRef } from "react";

const steps = [
  {
    number: "01",
    title: "Order",
    description: "Choose your line. Select your quantity. Check out in under 2 minutes.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 6h18M16 10a4 4 0 01-8 0" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: "02",
    title: "Ship",
    description: "Orders process within 24 hours. Discreet packaging. Tracking included.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="5.5" cy="18.5" r="2.5" stroke="#3F5A45" strokeWidth="1.5" />
        <circle cx="18.5" cy="18.5" r="2.5" stroke="#3F5A45" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    number: "03",
    title: "Try",
    description: "Open your kilo and your free sample. Find your line. Experience the consistency.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M8 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V8l-5-5H8z" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 3v5h8M12 11v6M9 14h6" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: "04",
    title: "Guarantee",
    description: "If it's not for you within 30 days, we refund you. Simple as that.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default function HowItWorksSection() {
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    refs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28" style={{ backgroundColor: "#1F1F1F" }}>
      <div className="max-w-container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={(el) => { refs.current[0] = el; }}
          className="reveal mb-16 text-center"
        >
          <span
            className="text-xs font-semibold uppercase tracking-widest mb-4 block"
            style={{ color: "rgba(63,90,69,0.9)", fontFamily: "Inter, system-ui, sans-serif" }}
          >
            The Process
          </span>
          <h2
            className="font-serif font-bold text-white"
            style={{
              fontFamily: "Fraunces, Georgia, serif",
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              letterSpacing: "-0.025em",
              lineHeight: "1.1",
            }}
          >
            How It Works.
            <br />
            <em style={{ color: "#3F5A45" }}>Four steps. No friction.</em>
          </h2>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
          {steps.map((step, i) => (
            <div
              key={step.number}
              ref={(el) => { refs.current[i + 1] = el; }}
              className={`reveal stagger-${i + 1} process-step group`}
            >
              <div
                className="p-8 h-full transition-all duration-300 relative"
                style={{
                  borderRight: i < steps.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.backgroundColor = "rgba(63,90,69,0.08)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.backgroundColor = "transparent";
                }}
              >
                {/* Step number */}
                <div
                  className="text-xs font-semibold mb-6"
                  style={{
                    color: "rgba(255,255,255,0.2)",
                    fontFamily: "Inter, system-ui, sans-serif",
                    letterSpacing: "0.1em",
                  }}
                >
                  {step.number}
                </div>

                {/* Icon */}
                <div
                  className="w-14 h-14 flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: "rgba(63,90,69,0.12)",
                    border: "1px solid rgba(63,90,69,0.2)",
                    borderRadius: "2px",
                  }}
                >
                  {step.icon}
                </div>

                {/* Title */}
                <h3
                  className="font-serif font-bold text-white mb-3"
                  style={{
                    fontFamily: "Fraunces, Georgia, serif",
                    fontSize: "1.5rem",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {step.title}
                </h3>

                {/* Description */}
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "rgba(255,255,255,0.45)", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}